import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import API_BASE_URL, { API_BASE_URL_WITHOUT_API } from '../config/api';
import './HotelForm.css';

const HotelForm = ({ mode = 'admin', ownerToken = '', ownerPhone = '', onSuccess, onCancel, onCancelPath }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isOwnerMode = mode === 'owner';
  const isEdit = !!id && !isOwnerMode;
  const [activeTab, setActiveTab] = useState('basic');

  const [formData, setFormData] = useState({
    // Basic Information
    name: '',
    category: '',
    city: '',
    state: '',
    country: 'India',
    zipCode: '',
    address: '',
    displayAddress: '',
    description: '',
    shortDescription: '',
    
    // Location
    latitude: '',
    longitude: '',
    cityLatitude: '',
    cityLongitude: '',
    
    // Contact
    whatsappNumber: '',
    phone: ownerPhone || '',
    email: '',
    website: '',
    
    // Hotel Details
    starRating: null,
    totalRooms: 0,
    checkInTime: '11:00',
    checkOutTime: '10:00',
    
    // Policies
    cancellationPolicy: '',
    paymentPolicy: '',
    petPolicy: 'Pets not allowed',
    smokingPolicy: 'Non-smoking rooms available',
    ageRestriction: '',
    
    // Facilities (array of facility IDs)
    facilities: [],
    
    // Status
    status: isOwnerMode ? 'pending' : 'active',
    featured: false,
    verified: false,
    currency: 'INR',
    
    // Social Links
    socialLinks: {
      facebook: '',
      instagram: '',
      youtube: ''
    }
  });

  const [availableFacilities, setAvailableFacilities] = useState([]);
  const [facilitiesLoading, setFacilitiesLoading] = useState(true);
  const [availableHotelAmenities, setAvailableHotelAmenities] = useState([]);
  const [hotelAmenitiesLoading, setHotelAmenitiesLoading] = useState(true);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [verificationSubscriptions, setVerificationSubscriptions] = useState([]);
  const [featuredSubscriptions, setFeaturedSubscriptions] = useState([]);
  const [verificationSubscriptionId, setVerificationSubscriptionId] = useState('');
  const [featuredSubscriptionId, setFeaturedSubscriptionId] = useState('');

  const [amenities, setAmenities] = useState([]);
  const [amenityInput, setAmenityInput] = useState('');
  const [roomTypes, setRoomTypes] = useState([]);
  const [editingRoomTypeIndex, setEditingRoomTypeIndex] = useState(null);
  const [roomTypeInput, setRoomTypeInput] = useState({
    roomType: '',
    pricePerNight: '',
    cutPrice: '',
    maxPersons: '',
    bedType: '',
    roomSize: '',
    description: '',
    roomAmenities: [],
    additionalAmenities: []
  });
  const [roomAdditionalAmenityInput, setRoomAdditionalAmenityInput] = useState('');
  const [images, setImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [featuredImage, setFeaturedImage] = useState('');
  const [nearbyAttractions, setNearbyAttractions] = useState([]);
  const [attractionInput, setAttractionInput] = useState({ name: '', distance: '', description: '' });
  const [languages, setLanguages] = useState([]);
  const [languageInput, setLanguageInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [addressInput, setAddressInput] = useState('');
  const [citySuggestions, setCitySuggestions] = useState([]);
  const [showCitySuggestions, setShowCitySuggestions] = useState(false);
  const [cityInput, setCityInput] = useState('');
  const [keepDisplayAddressSame, setKeepDisplayAddressSame] = useState(true);
  const headerTitle = isOwnerMode ? 'Hotel Registration' : isEdit ? 'Edit Hotel' : 'Add New Hotel';

  useEffect(() => {
    if (isOwnerMode && ownerPhone) {
      setFormData(prev => ({
        ...prev,
        phone: ownerPhone,
        status: 'pending',
        featured: false,
        verified: false
      }));
    }
  }, [isOwnerMode, ownerPhone]);

  const fetchFacilities = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/public/facilities/public`);
      setAvailableFacilities(response.data.data || []);
      setFacilitiesLoading(false);
    } catch (error) {
      console.error('Error fetching facilities:', error);
      setFacilitiesLoading(false);
    }
  };

  const fetchHotelAmenities = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/public/amenities/public`, { params: { type: 'Hotel' } });
      setAvailableHotelAmenities(response.data.data || []);
      setHotelAmenitiesLoading(false);
    } catch (error) {
      console.error('Error fetching hotel amenities:', error);
      setHotelAmenitiesLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/public/hotel-categories/public`);
      setAvailableCategories(response.data.data || []);
      setCategoriesLoading(false);
    } catch (error) {
      console.error('Error fetching hotel categories:', error);
      setCategoriesLoading(false);
    }
  };

  const fetchSubscriptions = async () => {
    try {
      const [verificationRes, featuredRes] = await Promise.all([
        axios.get(`${API_BASE_URL}/admin/verification-subscriptions?type=Hotel`),
        axios.get(`${API_BASE_URL}/admin/featured-subscriptions?type=Hotel`)
      ]);
      setVerificationSubscriptions(verificationRes.data.data || []);
      setFeaturedSubscriptions(featuredRes.data.data || []);
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
    }
  };

  const fetchHotel = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin/hotels/${id}`);
      const hotel = response.data.data;
      
      // Extract facility IDs from populated facilities
      const facilityIds = hotel.facilities 
        ? hotel.facilities.map(f => typeof f === 'object' ? f._id : f).filter(Boolean)
        : [];
      
      setFormData({
        name: hotel.name || '',
        category: hotel.category ? (typeof hotel.category === 'object' ? hotel.category._id : hotel.category) : '',
        city: hotel.city || '',
        state: hotel.state || '',
        country: hotel.country || 'India',
        zipCode: hotel.zipCode || '',
        address: hotel.address || '',
        displayAddress: hotel.displayAddress || hotel.address || '',
        description: hotel.description || '',
        shortDescription: hotel.shortDescription || '',
        latitude: hotel.latitude || '',
        longitude: hotel.longitude || '',
        cityLatitude: hotel.cityLatitude || '',
        cityLongitude: hotel.cityLongitude || '',
        whatsappNumber: hotel.whatsappNumber || '',
        phone: hotel.phone || '',
        email: hotel.email || '',
        website: hotel.website || '',
        starRating: hotel.starRating !== undefined && hotel.starRating !== null ? hotel.starRating : null,
        totalRooms: hotel.totalRooms || 0,
        checkInTime: hotel.checkInTime || '11:00',
        checkOutTime: hotel.checkOutTime || '10:00',
        cancellationPolicy: hotel.cancellationPolicy || '',
        paymentPolicy: hotel.paymentPolicy || '',
        petPolicy: hotel.petPolicy || 'Pets not allowed',
        smokingPolicy: hotel.smokingPolicy || 'Non-smoking rooms available',
        ageRestriction: hotel.ageRestriction || '',
        facilities: facilityIds,
        status: hotel.status || 'active',
        featured: hotel.featured || false,
        verified: hotel.verified || false,
        currency: hotel.currency || 'INR',
        socialLinks: hotel.socialLinks || {
          facebook: '',
          instagram: '',
          youtube: ''
        }
      });
      
      setAmenities(hotel.amenities || []);
      // Normalize roomTypes.roomAmenities to array of amenity IDs (populated objects have _id; ignore legacy strings)
      const toAmenityIds = (arr) => (Array.isArray(arr) ? arr : [])
        .map(a => (a && typeof a === 'object' && a._id) ? a._id : a)
        .filter(id => id && typeof id === 'string' && /^[a-fA-F0-9]{24}$/.test(id));
      const normalizedRoomTypes = (hotel.roomTypes || []).map(rt => ({
        ...rt,
        roomAmenities: toAmenityIds(rt.roomAmenities),
        additionalAmenities: Array.isArray(rt.additionalAmenities) ? rt.additionalAmenities : []
      }));
      setRoomTypes(normalizedRoomTypes);
      setExistingImages(hotel.images || []);
      setFeaturedImage(hotel.featuredImage || '');
      setNearbyAttractions(hotel.nearbyAttractions || []);
      setLanguages(hotel.languages || []);
      setAddressInput(hotel.address || '');
      setCityInput(hotel.city || '');
      setKeepDisplayAddressSame(hotel.displayAddress === hotel.address || !hotel.displayAddress);
    } catch (error) {
      setError('Failed to fetch hotel details');
    }
  };

  useEffect(() => {
    const loadData = async () => {
      await fetchFacilities();
      await fetchHotelAmenities();
      await fetchCategories();
      if (!isOwnerMode) {
        await fetchSubscriptions();
      }
      if (isEdit && id) {
        await fetchHotel();
      }
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isOwnerMode]);

  // Set subscription IDs after subscriptions are loaded (for edit mode)
  useEffect(() => {
    if (!isOwnerMode && isEdit && id && verificationSubscriptions.length > 0 && featuredSubscriptions.length > 0) {
      // Fetch hotel again to get subscription IDs once subscriptions are loaded
      axios.get(`${API_BASE_URL}/admin/hotels/${id}`)
        .then(response => {
          const hotel = response.data.data;
          if (hotel.verificationSubscription) {
            const subId = typeof hotel.verificationSubscription === 'object' 
              ? hotel.verificationSubscription._id 
              : hotel.verificationSubscription;
            setVerificationSubscriptionId(subId);
          }
          if (hotel.featuredSubscription) {
            const subId = typeof hotel.featuredSubscription === 'object' 
              ? hotel.featuredSubscription._id 
              : hotel.featuredSubscription;
            setFeaturedSubscriptionId(subId);
          }
        })
        .catch(error => console.error('Error fetching hotel subscriptions:', error));
    }
  }, [isOwnerMode, isEdit, id, verificationSubscriptions.length, featuredSubscriptions.length]);

  // Cleanup image preview URLs on unmount
  useEffect(() => {
    return () => {
      imagePreviews.forEach(url => URL.revokeObjectURL(url));
    };
  }, [imagePreviews]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.startsWith('facility_')) {
      const facilityId = name.replace('facility_', '');
      setFormData({
        ...formData,
        facilities: checked
          ? [...formData.facilities, facilityId]
          : formData.facilities.filter(id => id !== facilityId)
      });
    } else if (name === 'starRating') {
      // Handle starRating - empty string means null (unknown)
      setFormData({
        ...formData,
        [name]: value === '' ? null : parseInt(value)
      });
    } else if (name.startsWith('socialLinks.')) {
      // Handle social links
      const socialField = name.replace('socialLinks.', '');
      setFormData({
        ...formData,
        socialLinks: {
          ...formData.socialLinks,
          [socialField]: value
        }
      });
    } else {
      setFormData({
        ...formData,
        [name]: type === 'checkbox' ? checked : (type === 'number' ? parseFloat(value) || 0 : value)
      });
    }
  };

  const handleAddressInputChange = async (e) => {
    const value = e.target.value;
    setAddressInput(value);
    setFormData({
      ...formData,
      address: value,
      displayAddress: keepDisplayAddressSame ? value : formData.displayAddress
    });

    if (value.length > 2) {
      try {
        const response = await axios.get(`${API_BASE_URL}/public/place/autocomplete`, {
          params: { input: value }
        });
        setAddressSuggestions(response.data.data || []);
        setShowSuggestions(true);
      } catch (error) {
        console.error('Error fetching autocomplete:', error);
        // Don't show error if API key is not configured
        if (error.response?.status !== 400) {
          setAddressSuggestions([]);
        }
      }
    } else {
      setAddressSuggestions([]);
      setShowSuggestions(false);
    }
  };

  const handlePlaceSelect = async (place) => {
    setAddressInput(place.description);
    const newAddress = place.description;
    setFormData({
      ...formData,
      address: newAddress,
      displayAddress: keepDisplayAddressSame ? newAddress : formData.displayAddress
    });
    setShowSuggestions(false);
    setAddressSuggestions([]);

    try {
      const response = await axios.get(`${API_BASE_URL}/public/place/details`, {
        params: { placeId: place.place_id }
      });
      
      const placeDetails = response.data.data;
      setFormData({
        ...formData,
        address: newAddress,
        displayAddress: keepDisplayAddressSame ? newAddress : formData.displayAddress,
        // Only update hotel coordinates, not city information
        latitude: placeDetails.latitude || formData.latitude,
        longitude: placeDetails.longitude || formData.longitude
      });
    } catch (error) {
      console.error('Error fetching place details:', error);
      // Still update address even if details fail
    }
  };

  const handleCityInputChange = async (e) => {
    const value = e.target.value;
    setCityInput(value);
    setFormData({
      ...formData,
      city: value
    });

    if (value.length > 2) {
      try {
        const response = await axios.get(`${API_BASE_URL}/public/place/autocomplete`, {
          params: { input: value }
        });
        setCitySuggestions(response.data.data || []);
        setShowCitySuggestions(true);
      } catch (error) {
        console.error('Error fetching city autocomplete:', error);
        if (error.response?.status !== 400) {
          setCitySuggestions([]);
        }
      }
    } else {
      setCitySuggestions([]);
      setShowCitySuggestions(false);
    }
  };

  const handleCitySelect = async (place) => {
    setCityInput(place.description);
    setFormData({
      ...formData,
      city: place.description
    });
    setShowCitySuggestions(false);
    setCitySuggestions([]);

    try {
      const response = await axios.get(`${API_BASE_URL}/public/place/details`, {
        params: { placeId: place.place_id }
      });
      
      const placeDetails = response.data.data;
      setFormData({
        ...formData,
        city: place.description,
        state: placeDetails.state || formData.state,
        country: placeDetails.country || formData.country,
        cityLatitude: placeDetails.latitude || formData.cityLatitude,
        cityLongitude: placeDetails.longitude || formData.cityLongitude
      });
    } catch (error) {
      console.error('Error fetching city place details:', error);
    }
  };

  const handleAddAmenity = () => {
    if (amenityInput.trim()) {
      setAmenities([...amenities, amenityInput.trim()]);
      setAmenityInput('');
    }
  };

  const handleRemoveAmenity = (index) => {
    setAmenities(amenities.filter((_, i) => i !== index));
  };

  const handleRoomAmenityToggle = (amenityId) => {
    const current = roomTypeInput.roomAmenities || [];
    const next = current.includes(amenityId)
      ? current.filter(id => id !== amenityId)
      : [...current, amenityId];
    setRoomTypeInput({ ...roomTypeInput, roomAmenities: next });
  };

  const handleAddRoomAdditionalAmenity = () => {
    if (roomAdditionalAmenityInput.trim()) {
      setRoomTypeInput({
        ...roomTypeInput,
        additionalAmenities: [...(roomTypeInput.additionalAmenities || []), roomAdditionalAmenityInput.trim()]
      });
      setRoomAdditionalAmenityInput('');
    }
  };

  const handleRemoveRoomAdditionalAmenity = (index) => {
    setRoomTypeInput({
      ...roomTypeInput,
      additionalAmenities: (roomTypeInput.additionalAmenities || []).filter((_, i) => i !== index)
    });
  };

  const handleEditRoomType = (index) => {
    // If already editing a different room type, cancel that first
    if (editingRoomTypeIndex !== null && editingRoomTypeIndex !== index) {
      handleCancelEditRoomType();
    }
    
    const roomType = roomTypes[index];
    setRoomTypeInput({
      roomType: roomType.roomType || '',
      pricePerNight: roomType.pricePerNight || '',
      cutPrice: roomType.cutPrice || '',
      maxPersons: roomType.maxPersons || '',
      bedType: roomType.bedType || '',
      roomSize: roomType.roomSize || '',
      description: roomType.description || '',
      roomAmenities: Array.isArray(roomType.roomAmenities) ? [...roomType.roomAmenities] : [],
      additionalAmenities: Array.isArray(roomType.additionalAmenities) ? [...roomType.additionalAmenities] : []
    });
    setEditingRoomTypeIndex(index);
    // Scroll to form
    document.querySelector('.room-type-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleCancelEditRoomType = () => {
    setEditingRoomTypeIndex(null);
    setRoomTypeInput({
      roomType: '',
      pricePerNight: '',
      cutPrice: '',
      maxPersons: '',
      bedType: '',
      roomSize: '',
      description: '',
      roomAmenities: [],
      additionalAmenities: []
    });
    setRoomAdditionalAmenityInput('');
  };

  const handleAddRoomType = () => {
    if (roomTypeInput.roomType && roomTypeInput.pricePerNight && roomTypeInput.maxPersons) {
      const newRoomType = {
        roomType: roomTypeInput.roomType,
        pricePerNight: parseFloat(roomTypeInput.pricePerNight),
        cutPrice: roomTypeInput.cutPrice ? parseFloat(roomTypeInput.cutPrice) : null,
        maxPersons: parseInt(roomTypeInput.maxPersons),
        bedType: roomTypeInput.bedType || '',
        roomSize: roomTypeInput.roomSize || '',
        description: roomTypeInput.description || '',
        roomAmenities: Array.isArray(roomTypeInput.roomAmenities) ? roomTypeInput.roomAmenities : [],
        additionalAmenities: Array.isArray(roomTypeInput.additionalAmenities) ? roomTypeInput.additionalAmenities : [],
        roomImages: editingRoomTypeIndex !== null ? roomTypes[editingRoomTypeIndex].roomImages : []
      };

      if (editingRoomTypeIndex !== null) {
        // Update existing room type
        const updatedRoomTypes = [...roomTypes];
        updatedRoomTypes[editingRoomTypeIndex] = newRoomType;
        setRoomTypes(updatedRoomTypes);
        setEditingRoomTypeIndex(null);
      } else {
        // Add new room type
        setRoomTypes([...roomTypes, newRoomType]);
      }

      // Reset form
      setRoomTypeInput({
        roomType: '',
        pricePerNight: '',
        cutPrice: '',
        maxPersons: '',
        bedType: '',
        roomSize: '',
        description: '',
        roomAmenities: [],
        additionalAmenities: []
      });
      setRoomAdditionalAmenityInput('');
    }
  };

  const handleRemoveRoomType = (index) => {
    if (editingRoomTypeIndex === index) {
      handleCancelEditRoomType();
    }
    setRoomTypes(roomTypes.filter((_, i) => i !== index));
  };

  const handleAddAttraction = () => {
    if (attractionInput.name.trim()) {
      setNearbyAttractions([...nearbyAttractions, { ...attractionInput }]);
      setAttractionInput({ name: '', distance: '', description: '' });
    }
  };

  const handleRemoveAttraction = (index) => {
    setNearbyAttractions(nearbyAttractions.filter((_, i) => i !== index));
  };

  const handleAddLanguage = () => {
    if (languageInput.trim()) {
      setLanguages([...languages, languageInput.trim()]);
      setLanguageInput('');
    }
  };

  const handleRemoveLanguage = (index) => {
    setLanguages(languages.filter((_, i) => i !== index));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    setImages(files);
    
    // Create preview URLs for selected images
    const previews = files.map(file => URL.createObjectURL(file));
    setImagePreviews(previews);
  };

  const handleRemoveNewImage = (index) => {
    // Revoke the object URL to free memory
    if (imagePreviews[index]) {
      URL.revokeObjectURL(imagePreviews[index]);
    }
    
    setImages(images.filter((_, i) => i !== index));
    setImagePreviews(imagePreviews.filter((_, i) => i !== index));
    
    // Reset file input
    const fileInput = document.querySelector('input[type="file"][accept="image/*"]');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleSetFeaturedFromNew = (index) => {
    // Mark which new image should be featured
    // We'll use the index to identify it after upload
    setFeaturedImage(`new_image_${index}`);
  };

  const handleRemoveExistingImage = (index) => {
    setExistingImages(existingImages.filter((_, i) => i !== index));
  };

  const handleSetFeaturedImage = (imagePath) => {
    setFeaturedImage(imagePath);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (isOwnerMode && !ownerToken) {
        setError('Please verify your phone number to continue.');
        setLoading(false);
        return;
      }

      // Validation for required fields
      const validationErrors = [];

      // Validate Hotel Name
      if (!formData.name || !formData.name.trim()) {
        validationErrors.push('Hotel Name is required');
      }

      // Validate Hotel Category
      if (!formData.category || !formData.category.trim()) {
        validationErrors.push('Hotel Category is required');
      }

      // Validate Room Types
      if (!roomTypes || roomTypes.length === 0) {
        validationErrors.push('At least one Room Type is required');
      } else {
        // Validate that each room type has required fields
        const invalidRoomTypes = roomTypes.filter(rt => !rt.roomType || !rt.roomType.trim() || !rt.pricePerNight || !rt.maxPersons);
        if (invalidRoomTypes.length > 0) {
          validationErrors.push('All room types must have Room Type name, Price per Night, and Max Persons');
        }
      }

      // Validate Images
      const totalImages = (images && images.length) + (existingImages && existingImages.length);
      if (totalImages === 0) {
        validationErrors.push('At least one Image is required');
      }

      // If there are validation errors, show them and stop submission
      if (validationErrors.length > 0) {
        setError(validationErrors.join('. '));
        setLoading(false);
        // Scroll to error message
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      const submitData = new FormData();
      
      // Basic Information
      submitData.append('name', formData.name);
      submitData.append('category', formData.category || '');
      submitData.append('city', formData.city);
      submitData.append('state', formData.state);
      submitData.append('country', formData.country);
      submitData.append('zipCode', formData.zipCode);
      submitData.append('address', formData.address);
      submitData.append('displayAddress', formData.displayAddress || formData.address);
      submitData.append('description', formData.description);
      submitData.append('shortDescription', formData.shortDescription);
      
      // Location
      submitData.append('latitude', formData.latitude);
      submitData.append('longitude', formData.longitude);
      submitData.append('cityLatitude', formData.cityLatitude);
      submitData.append('cityLongitude', formData.cityLongitude);
      
      // Contact
      submitData.append('whatsappNumber', formData.whatsappNumber);
      submitData.append('phone', isOwnerMode ? ownerPhone : formData.phone);
      submitData.append('email', formData.email);
      submitData.append('website', formData.website);
      
      // Hotel Details
      submitData.append('starRating', formData.starRating === null ? '' : formData.starRating);
      submitData.append('totalRooms', formData.totalRooms);
      submitData.append('checkInTime', formData.checkInTime);
      submitData.append('checkOutTime', formData.checkOutTime);
      
      // Policies
      submitData.append('cancellationPolicy', formData.cancellationPolicy);
      submitData.append('paymentPolicy', formData.paymentPolicy);
      submitData.append('petPolicy', formData.petPolicy);
      submitData.append('smokingPolicy', formData.smokingPolicy);
      submitData.append('ageRestriction', formData.ageRestriction);
      
      // Facilities & Amenities
      submitData.append('amenities', JSON.stringify(amenities));
      submitData.append('facilities', JSON.stringify(formData.facilities));
      
      // Room Types
      submitData.append('roomTypes', JSON.stringify(roomTypes));
      
      // Additional
      submitData.append('nearbyAttractions', JSON.stringify(nearbyAttractions));
      submitData.append('languages', JSON.stringify(languages));
      submitData.append('currency', formData.currency);
      const resolvedStatus = isOwnerMode ? 'pending' : formData.status;
      const resolvedFeatured = isOwnerMode ? false : formData.featured;
      const resolvedVerified = isOwnerMode ? false : formData.verified;
      submitData.append('status', resolvedStatus);
      submitData.append('featured', resolvedFeatured);
      submitData.append('verified', resolvedVerified);
      submitData.append('socialLinks', JSON.stringify(formData.socialLinks));
      if (!isOwnerMode && resolvedFeatured && featuredSubscriptionId) {
        submitData.append('featuredSubscriptionId', featuredSubscriptionId);
      }
      if (!isOwnerMode && resolvedVerified && verificationSubscriptionId) {
        submitData.append('verificationSubscriptionId', verificationSubscriptionId);
      }
      // Determine featured image and reorder new images if needed
      let finalFeaturedImage = featuredImage;
      let imagesToUpload = [...images];
      let previewsToKeep = [...imagePreviews];
      
      // If a new image is selected as featured, move it to the front
      if (finalFeaturedImage && finalFeaturedImage.startsWith('new_image_')) {
        const featuredIndex = parseInt(finalFeaturedImage.replace('new_image_', ''));
        if (featuredIndex >= 0 && featuredIndex < imagesToUpload.length) {
          // Move featured image to first position
          const [featuredImage] = imagesToUpload.splice(featuredIndex, 1);
          const [featuredPreview] = previewsToKeep.splice(featuredIndex, 1);
          imagesToUpload.unshift(featuredImage);
          previewsToKeep.unshift(featuredPreview);
          // Backend will use first image as featured
          finalFeaturedImage = '';
        }
      }
      
      // If no featured image set, use first existing image
      if (!finalFeaturedImage && existingImages.length > 0) {
        finalFeaturedImage = existingImages[0];
      }
      
      submitData.append('featuredImage', finalFeaturedImage || '');
      submitData.append('existingImages', JSON.stringify(existingImages));

      imagesToUpload.forEach((image) => {
        submitData.append('images', image);
      });
      
      // Clean up preview URLs
      previewsToKeep.forEach(url => URL.revokeObjectURL(url));

      const headers = {
        'Content-Type': 'multipart/form-data',
        ...(isOwnerMode && ownerToken ? { Authorization: `Bearer ${ownerToken}` } : {})
      };

      if (isOwnerMode) {
        await axios.post(`${API_BASE_URL}/public/owners/hotels`, submitData, { headers });
        if (onSuccess) {
          onSuccess();
        } else {
          navigate('/');
        }
      } else if (isEdit) {
        await axios.put(`${API_BASE_URL}/admin/hotels/${id}`, submitData, { headers });
        navigate('/sy-admin/hotels');
      } else {
        await axios.post(`${API_BASE_URL}/admin/hotels`, submitData, { headers });
        navigate('/sy-admin/hotels');
      }
    } catch (error) {
      // Extract error message from response
      let errorMessage = 'Failed to save hotel';
      
      if (error.response) {
        // Server responded with error
        if (error.response.data) {
          if (typeof error.response.data === 'string') {
            errorMessage = error.response.data;
          } else if (error.response.data.message) {
            errorMessage = error.response.data.message;
          } else if (error.response.data.error) {
            errorMessage = error.response.data.error;
          } else if (Array.isArray(error.response.data.errors)) {
            // Handle validation errors array
            errorMessage = error.response.data.errors.map(err => err.msg || err.message || err).join('. ');
          }
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      setError(errorMessage);
      setLoading(false);
      // Scroll to error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleCancel = () => {
    if (isOwnerMode) {
      if (onCancel) {
        onCancel();
        return;
      }
      if (onCancelPath) {
        navigate(onCancelPath);
        return;
      }
      navigate('/');
      return;
    }
    navigate('/sy-admin/hotels');
  };

  const tabs = [
    { id: 'basic', label: 'Basic Info' },
    { id: 'location', label: 'Location' },
    { id: 'contact', label: 'Contact' },
    { id: 'details', label: 'Details & Policies' },
    { id: 'facilities', label: 'Facilities' },
    { id: 'rooms', label: 'Room Types' },
    { id: 'media', label: 'Media' },
    { id: 'social', label: 'Social Links' },
    { id: 'additional', label: 'Additional' }
  ];

  const isLastTab = activeTab === tabs[tabs.length - 1].id;
  const currentTabIndex = tabs.findIndex(tab => tab.id === activeTab);
  const nextTab = currentTabIndex < tabs.length - 1 ? tabs[currentTabIndex + 1] : null;

  // Validate current tab before moving to next
  const validateCurrentTab = (tabId) => {
    const errors = [];

    switch (tabId) {
      case 'basic':
        if (!formData.name || !formData.name.trim()) {
          errors.push('Hotel Name is required');
        }
        if (!formData.category || !formData.category.trim()) {
          errors.push('Hotel Category is required');
        }
        break;
      case 'location':
        if (!formData.address || !formData.address.trim()) {
          errors.push('Address is required');
        }
        if (!formData.city || !formData.city.trim()) {
          errors.push('City is required');
        }
        if (!formData.state || !formData.state.trim()) {
          errors.push('State is required');
        }
        break;
      case 'contact':
        if (!isOwnerMode && (!formData.phone || !formData.phone.trim())) {
          errors.push('Phone number is required');
        }
        break;
      case 'rooms':
        if (!roomTypes || roomTypes.length === 0) {
          errors.push('At least one Room Type is required');
        } else {
          const invalidRoomTypes = roomTypes.filter(rt => !rt.roomType || !rt.roomType.trim() || !rt.pricePerNight || !rt.maxPersons);
          if (invalidRoomTypes.length > 0) {
            errors.push('All room types must have Room Type name, Price per Night, and Max Persons');
          }
        }
        break;
      case 'media':
        const totalImages = (images && images.length) + (existingImages && existingImages.length);
        if (totalImages === 0) {
          errors.push('At least one Image is required');
        }
        break;
      default:
        // Other tabs (details, facilities, social, additional) don't have required fields
        break;
    }

    return errors;
  };

  const handleNext = (e) => {
    // Prevent form submission
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    // Don't proceed if already on last tab
    if (isLastTab) {
      return false;
    }

    // Validate current tab before moving to next
    const validationErrors = validateCurrentTab(activeTab);
    
    if (validationErrors.length > 0) {
      setError(validationErrors.join('. '));
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return false;
    }

    // Clear any previous errors
    setError('');

    if (nextTab) {
      setActiveTab(nextTab.id);
      // Scroll to top of form
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    
    return false;
  };

  const content = (
    <div className="container">
      <div className="hotel-form-header">
        <h1>{headerTitle}</h1>
        {isOwnerMode && (
          <p className="form-hint" style={{ marginTop: '8px' }}>
            Complete your listing details to get your property verified and published.
          </p>
        )}
      </div>

        {error && (
          <div className="error-message" style={{ 
            padding: '16px 20px', 
            backgroundColor: '#fee2e2', 
            border: '1px solid #fca5a5', 
            borderRadius: '8px', 
            color: '#991b1b',
            marginBottom: '24px',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            fontSize: '15px',
            fontWeight: '500'
          }}>
            <span style={{ fontSize: '20px' }}>⚠️</span>
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="hotel-form">
          {/* Tab Navigation */}
          <div className="form-tabs">
            {tabs.map(tab => (
              <button
                key={tab.id}
                type="button"
                className={`tab-btn ${activeTab === tab.id ? 'active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Tab Content */}
          <div className="tab-content">
            {/* Basic Information Tab */}
            {activeTab === 'basic' && (
              <div className="form-section">
                <h2>Basic Information</h2>
                <div className="form-row">
                  <div className="form-group">
                    <label>Hotel Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Hotel Category *</label>
                    <select 
                      name="category" 
                      value={formData.category} 
                      onChange={handleChange}
                      required
                    >
                      <option value="">Select Category</option>
                      {availableCategories
                        .filter(cat => cat.isActive)
                        .map(category => (
                          <option key={category._id} value={category._id}>
                            {category.name}
                          </option>
                        ))}
                    </select>
                    {categoriesLoading && <small className="form-hint">Loading categories...</small>}
                    {!categoriesLoading && availableCategories.length === 0 && (
                      <small className="form-hint">
                        No categories available. <Link to="/sy-admin/hotel-categories">Add categories</Link>
                      </small>
                    )}
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Star Rating</label>
                    <select name="starRating" value={formData.starRating || ''} onChange={handleChange}>
                      <option value="">Unknown</option>
                      <option value={1}>1 Star</option>
                      <option value={2}>2 Stars</option>
                      <option value={3}>3 Stars</option>
                      <option value={4}>4 Stars</option>
                      <option value={5}>5 Stars</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label>Short Description</label>
                  <input
                    type="text"
                    name="shortDescription"
                    value={formData.shortDescription}
                    onChange={handleChange}
                    placeholder="Brief description (for listings)"
                    maxLength={200}
                  />
                </div>

                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleChange}
                    rows="6"
                    placeholder="Detailed description of the hotel"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Total Rooms</label>
                    <input
                      type="number"
                      name="totalRooms"
                      value={formData.totalRooms}
                      onChange={handleChange}
                      min="0"
                    />
                  </div>
                  <div className="form-group">
                    <label>Currency</label>
                    <select name="currency" value={formData.currency} onChange={handleChange}>
                      <option value="INR">INR (₹)</option>
                      <option value="USD">USD ($)</option>
                      <option value="EUR">EUR (€)</option>
                      <option value="GBP">GBP (£)</option>
                    </select>
                  </div>
                </div>

                {!isOwnerMode && (
                  <div className="form-row">
                    <div className="form-group">
                      <label>Status</label>
                      <select name="status" value={formData.status} onChange={handleChange}>
                        <option value="active">Active</option>
                        <option value="inactive">Inactive</option>
                        <option value="pending">Pending</option>
                      </select>
                    </div>
                  </div>
                )}

                {!isOwnerMode && (
                  <div className="subscription-switches-container">
                    <div className="subscription-switch-group">
                      <label className="subscription-switch-label">
                        <div className="subscription-switch-content">
                          <div className="subscription-switch-header">
                            <span className="subscription-switch-icon">✓</span>
                            <span className="subscription-switch-title">Verified Hotel</span>
                          </div>
                          <div className="toggle-switch">
                            <input
                              type="checkbox"
                              name="verified"
                              checked={formData.verified}
                              onChange={handleChange}
                              className="toggle-input"
                            />
                            <span className="toggle-slider"></span>
                          </div>
                        </div>
                      </label>
                      {formData.verified && (
                        <div className="subscription-select-wrapper">
                          <select
                            value={verificationSubscriptionId}
                            onChange={(e) => setVerificationSubscriptionId(e.target.value)}
                            className="subscription-select"
                            required
                          >
                            <option value="">Select Verification Subscription *</option>
                            {verificationSubscriptions
                              .filter(sub => sub.status === 'active')
                              .map(sub => (
                                <option key={sub._id} value={sub._id}>
                                  {sub.name} - {sub.duration} {sub.durationUnit} - {sub.currency === 'INR' ? '₹' : sub.currency} {sub.price}
                                </option>
                              ))}
                          </select>
                        </div>
                      )}
                    </div>

                    <div className="subscription-switch-group">
                      <label className="subscription-switch-label">
                        <div className="subscription-switch-content">
                          <div className="subscription-switch-header">
                            <span className="subscription-switch-icon">⭐</span>
                            <span className="subscription-switch-title">Featured Hotel</span>
                          </div>
                          <div className="toggle-switch">
                            <input
                              type="checkbox"
                              name="featured"
                              checked={formData.featured}
                              onChange={handleChange}
                              className="toggle-input"
                            />
                            <span className="toggle-slider"></span>
                          </div>
                        </div>
                      </label>
                      {formData.featured && (
                        <div className="subscription-select-wrapper">
                          <select
                            value={featuredSubscriptionId}
                            onChange={(e) => setFeaturedSubscriptionId(e.target.value)}
                            className="subscription-select"
                            required
                          >
                            <option value="">Select Featured Subscription *</option>
                            {featuredSubscriptions
                              .filter(sub => sub.status === 'active')
                              .map(sub => (
                                <option key={sub._id} value={sub._id}>
                                  {sub.name} - {sub.duration} {sub.durationUnit} - {sub.currency === 'INR' ? '₹' : sub.currency} {sub.price}
                                </option>
                              ))}
                          </select>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Location Tab */}
            {activeTab === 'location' && (
              <div className="form-section">
                <h2>Location Details</h2>
                
                {/* Hotel Address Section */}
                <div style={{ marginBottom: '32px', paddingBottom: '24px', borderBottom: '1px solid #e2e8f0' }}>
                  <h3 style={{ marginBottom: '16px', color: '#0f172a', fontSize: '1.25rem' }}>Hotel Address</h3>
                  
                  <div className="form-group" style={{ position: 'relative' }}>
                    <label>Address *</label>
                    <input
                      type="text"
                      name="address"
                      value={addressInput || formData.address}
                      onChange={handleAddressInputChange}
                      onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                      onFocus={() => addressSuggestions.length > 0 && setShowSuggestions(true)}
                      required
                      placeholder="Enter the actual hotel location address..."
                    />
                    {showSuggestions && addressSuggestions.length > 0 && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        zIndex: 1000,
                        maxHeight: '300px',
                        overflowY: 'auto',
                        marginTop: '4px'
                      }}>
                        {addressSuggestions.map((suggestion, index) => (
                          <div
                            key={index}
                            onClick={() => handlePlaceSelect(suggestion)}
                            style={{
                              padding: '12px 16px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #f1f5f9',
                              transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f8fafc'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                          >
                            <div style={{ fontWeight: 500, color: '#0f172a' }}>
                              {suggestion.structured_formatting?.main_text || suggestion.description}
                            </div>
                            {suggestion.structured_formatting?.secondary_text && (
                              <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                                {suggestion.structured_formatting.secondary_text}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    <small className="form-hint">Start typing to get address suggestions. This will auto-fill hotel coordinates.</small>
                  </div>

                  <div className="form-group">
                    <label>
                      <input
                        type="checkbox"
                        checked={keepDisplayAddressSame}
                        onChange={(e) => {
                          setKeepDisplayAddressSame(e.target.checked);
                          if (e.target.checked) {
                            setFormData({
                              ...formData,
                              displayAddress: formData.address
                            });
                          }
                        }}
                        style={{ marginRight: '8px' }}
                      />
                      Keep Display Address same as Address
                    </label>
                  </div>

                  <div className="form-group">
                    <label>Display Address</label>
                    <input
                      type="text"
                      name="displayAddress"
                      value={formData.displayAddress}
                      onChange={(e) => {
                        setFormData({
                          ...formData,
                          displayAddress: e.target.value
                        });
                        if (keepDisplayAddressSame) {
                          setKeepDisplayAddressSame(false);
                        }
                      }}
                      disabled={keepDisplayAddressSame}
                      placeholder="Enter display address (can be different from actual address)"
                    />
                    <small className="form-hint">Address to display to users (can be different from actual address)</small>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Hotel Latitude</label>
                      <input
                        type="number"
                        name="latitude"
                        value={formData.latitude}
                        onChange={handleChange}
                        step="any"
                        placeholder="e.g., 28.6139"
                      />
                      <small className="form-hint">Hotel's exact latitude (auto-filled from address)</small>
                    </div>
                    <div className="form-group">
                      <label>Hotel Longitude</label>
                      <input
                        type="number"
                        name="longitude"
                        value={formData.longitude}
                        onChange={handleChange}
                        step="any"
                        placeholder="e.g., 77.2090"
                      />
                      <small className="form-hint">Hotel's exact longitude (auto-filled from address)</small>
                    </div>
                  </div>
                </div>

                {/* City Information Section */}
                <div>
                  <h3 style={{ marginBottom: '16px', color: '#0f172a', fontSize: '1.25rem' }}>City Information</h3>
                  
                  <div className="form-group" style={{ position: 'relative' }}>
                    <label>City *</label>
                    <input
                      type="text"
                      name="city"
                      value={cityInput || formData.city}
                      onChange={handleCityInputChange}
                      onBlur={() => setTimeout(() => setShowCitySuggestions(false), 200)}
                      onFocus={() => citySuggestions.length > 0 && setShowCitySuggestions(true)}
                      required
                      placeholder="Enter city name..."
                    />
                    {showCitySuggestions && citySuggestions.length > 0 && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #e2e8f0',
                        borderRadius: '8px',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                        zIndex: 1000,
                        maxHeight: '300px',
                        overflowY: 'auto',
                        marginTop: '4px'
                      }}>
                        {citySuggestions.map((suggestion, index) => (
                          <div
                            key={index}
                            onClick={() => handleCitySelect(suggestion)}
                            style={{
                              padding: '12px 16px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #f1f5f9',
                              transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f8fafc'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                          >
                            <div style={{ fontWeight: 500, color: '#0f172a' }}>
                              {suggestion.structured_formatting?.main_text || suggestion.description}
                            </div>
                            {suggestion.structured_formatting?.secondary_text && (
                              <div style={{ fontSize: '12px', color: '#64748b', marginTop: '4px' }}>
                                {suggestion.structured_formatting.secondary_text}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    <small className="form-hint">Start typing city name to get suggestions. This will auto-fill city coordinates.</small>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>State</label>
                      <input
                        type="text"
                        name="state"
                        value={formData.state}
                        onChange={handleChange}
                      />
                    </div>
                    <div className="form-group">
                      <label>Country</label>
                      <input
                        type="text"
                        name="country"
                        value={formData.country}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Zip Code</label>
                    <input
                      type="text"
                      name="zipCode"
                      value={formData.zipCode}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>City Latitude</label>
                      <input
                        type="number"
                        name="cityLatitude"
                        value={formData.cityLatitude}
                        onChange={handleChange}
                        step="any"
                        placeholder="e.g., 28.6139"
                      />
                      <small className="form-hint">City center latitude (auto-filled from city selection)</small>
                    </div>
                    <div className="form-group">
                      <label>City Longitude</label>
                      <input
                        type="number"
                        name="cityLongitude"
                        value={formData.cityLongitude}
                        onChange={handleChange}
                        step="any"
                        placeholder="e.g., 77.2090"
                      />
                      <small className="form-hint">City center longitude (auto-filled from city selection)</small>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Contact Tab */}
            {activeTab === 'contact' && (
              <div className="form-section">
                <h2>Contact Information</h2>
                <div className="form-group">
                  <label>WhatsApp Number *</label>
                  <input
                    type="text"
                    name="whatsappNumber"
                    value={formData.whatsappNumber}
                    onChange={handleChange}
                    required
                    placeholder="e.g., +919876543210"
                  />
                </div>
                <div className="form-group">
                  <label>Phone Number *</label>
                  <input
                    type="text"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required={isOwnerMode}
                    readOnly={isOwnerMode}
                    placeholder="e.g., +919876543210"
                  />
                  {isOwnerMode && (
                    <small className="form-hint">This number is used for listing authentication.</small>
                  )}
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="hotel@example.com"
                  />
                </div>
                <div className="form-group">
                  <label>Website</label>
                  <input
                    type="url"
                    name="website"
                    value={formData.website}
                    onChange={handleChange}
                    placeholder="https://www.example.com"
                  />
                </div>
              </div>
            )}

            {/* Details & Policies Tab */}
            {activeTab === 'details' && (
              <div className="form-section">
                <h2>Hotel Details & Policies</h2>
                <div className="form-row">
                  <div className="form-group">
                    <label>Check-in Time</label>
                    <input
                      type="time"
                      name="checkInTime"
                      value={formData.checkInTime}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Check-out Time</label>
                    <input
                      type="time"
                      name="checkOutTime"
                      value={formData.checkOutTime}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Cancellation Policy</label>
                  <textarea
                    name="cancellationPolicy"
                    value={formData.cancellationPolicy}
                    onChange={handleChange}
                    rows="3"
                    placeholder="Describe cancellation policy"
                  />
                </div>

                <div className="form-group">
                  <label>Payment Policy</label>
                  <textarea
                    name="paymentPolicy"
                    value={formData.paymentPolicy}
                    onChange={handleChange}
                    rows="3"
                    placeholder="Describe payment policy"
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Pet Policy</label>
                    <input
                      type="text"
                      name="petPolicy"
                      value={formData.petPolicy}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Smoking Policy</label>
                    <input
                      type="text"
                      name="smokingPolicy"
                      value={formData.smokingPolicy}
                      onChange={handleChange}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Age Restriction</label>
                  <input
                    type="text"
                    name="ageRestriction"
                    value={formData.ageRestriction}
                    onChange={handleChange}
                    placeholder="e.g., Children under 12 stay free"
                  />
                </div>
              </div>
            )}

            {/* Facilities Tab */}
            {activeTab === 'facilities' && (
              <div className="form-section">
                <h2>Facilities & Amenities</h2>
                
                {facilitiesLoading ? (
                  <div>Loading facilities...</div>
                ) : availableFacilities.length === 0 ? (
                  <div className="no-facilities">
                    <p>No facilities available. Please add facilities from the <Link to="/sy-admin/facilities">Facilities Management</Link> page.</p>
                  </div>
                ) : (
                  <>
                    {/* Group facilities by category */}
                    {['general', 'room', 'dining', 'recreation', 'business', 'transport', 'other'].map(category => {
                      const categoryFacilities = availableFacilities.filter(f => f.category === category);
                      if (categoryFacilities.length === 0) return null;
                      
                      return (
                        <div key={category} className="facility-category">
                          <h3>{category.charAt(0).toUpperCase() + category.slice(1)} Facilities</h3>
                          <div className="facilities-grid">
                            {categoryFacilities.map(facility => (
                              <label key={facility._id} className="facility-checkbox">
                                <input
                                  type="checkbox"
                                  name={`facility_${facility._id}`}
                                  checked={formData.facilities.includes(facility._id)}
                                  onChange={handleChange}
                                />
                                <span>
                                  {facility.icon && <span style={{ marginRight: '6px' }}>{facility.icon}</span>}
                                  {facility.name}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      );
                    })}
                  </>
                )}

                <div className="form-group" style={{ marginTop: '24px' }}>
                  <label>Additional Amenities</label>
                  <div className="amenity-input">
                    <input
                      type="text"
                      value={amenityInput}
                      onChange={(e) => setAmenityInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddAmenity())}
                      placeholder="Add amenity (e.g., Free WiFi, Parking)"
                    />
                    <button type="button" onClick={handleAddAmenity} className="btn btn-secondary">
                      Add
                    </button>
                  </div>
                  <div className="amenity-list">
                    {amenities.map((amenity, index) => (
                      <span key={index} className="badge badge-primary">
                        {amenity}
                        <button
                          type="button"
                          onClick={() => handleRemoveAmenity(index)}
                          className="remove-btn"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Room Types Tab */}
            {activeTab === 'rooms' && (
              <div className="form-section">
                <h2>Room Types *</h2>
                <p className="form-hint" style={{ marginBottom: '24px', color: '#64748b' }}>
                  At least one room type is required to create a hotel listing.
                </p>
                
                <div className="room-type-form">
                  <h3>{editingRoomTypeIndex !== null ? 'Edit Room Type' : 'Add New Room Type'}</h3>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Room Type *</label>
                      <input
                        type="text"
                        placeholder="e.g., Deluxe, Suite, Standard"
                        value={roomTypeInput.roomType}
                        onChange={(e) => setRoomTypeInput({ ...roomTypeInput, roomType: e.target.value })}
                      />
                    </div>
                    <div className="form-group">
                      <label>Price per Night *</label>
                      <input
                        type="number"
                        placeholder="0.00"
                        value={roomTypeInput.pricePerNight}
                        onChange={(e) => setRoomTypeInput({ ...roomTypeInput, pricePerNight: e.target.value })}
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div className="form-group">
                      <label>Cut Price (Original Price)</label>
                      <input
                        type="number"
                        placeholder="0.00"
                        value={roomTypeInput.cutPrice}
                        onChange={(e) => setRoomTypeInput({ ...roomTypeInput, cutPrice: e.target.value })}
                        min="0"
                        step="0.01"
                      />
                      <small className="form-hint">Original price before discount (optional)</small>
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Max Persons *</label>
                      <input
                        type="number"
                        placeholder="eg. 2"
                        value={roomTypeInput.maxPersons}
                        onChange={(e) => setRoomTypeInput({ ...roomTypeInput, maxPersons: e.target.value })}
                        min="1"
                      />
                    </div>
                    <div className="form-group">
                      <label>Bed Type</label>
                      <input
                        type="text"
                        placeholder="e.g., King, Queen, Twin"
                        value={roomTypeInput.bedType}
                        onChange={(e) => setRoomTypeInput({ ...roomTypeInput, bedType: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label>Room Size</label>
                      <input
                        type="text"
                        placeholder="e.g., 25 sqm"
                        value={roomTypeInput.roomSize}
                        onChange={(e) => setRoomTypeInput({ ...roomTypeInput, roomSize: e.target.value })}
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label>Room Description</label>
                    <textarea
                      placeholder="Describe the room"
                      value={roomTypeInput.description}
                      onChange={(e) => setRoomTypeInput({ ...roomTypeInput, description: e.target.value })}
                      rows="3"
                    />
                  </div>

                  <div className="form-group">
                    <label>Room Amenities</label>
                    {hotelAmenitiesLoading ? (
                      <div>Loading amenities...</div>
                    ) : availableHotelAmenities.length === 0 ? (
                      <div className="no-facilities" style={{ padding: '12px', background: '#fef3c7', borderRadius: '8px', color: '#92400e' }}>
                        No Hotel amenities available. Add amenities (type: Hotel) from <Link to="/sy-admin/amenities">Amenities Management</Link>.
                      </div>
                    ) : (
                      <div className="facilities-grid">
                        {availableHotelAmenities.map(amenity => (
                          <label key={amenity._id} className="facility-checkbox">
                            <input
                              type="checkbox"
                              checked={(roomTypeInput.roomAmenities || []).includes(amenity._id)}
                              onChange={() => handleRoomAmenityToggle(amenity._id)}
                            />
                            <span>
                              {amenity.icon && <span style={{ marginRight: '6px' }}>{amenity.icon}</span>}
                              {amenity.name}
                            </span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="form-group">
                    <label>Additional Amenities</label>
                    <div className="amenity-input">
                      <input
                        type="text"
                        value={roomAdditionalAmenityInput}
                        onChange={(e) => setRoomAdditionalAmenityInput(e.target.value)}
                        onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddRoomAdditionalAmenity())}
                        placeholder="Add amenity (e.g., Free WiFi, Minibar)"
                      />
                      <button type="button" onClick={handleAddRoomAdditionalAmenity} className="btn btn-secondary">
                        Add
                      </button>
                    </div>
                    <div className="amenity-list">
                      {(roomTypeInput.additionalAmenities || []).map((amenity, index) => (
                        <span key={index} className="badge badge-primary">
                          {amenity}
                          <button
                            type="button"
                            onClick={() => handleRemoveRoomAdditionalAmenity(index)}
                            className="remove-btn"
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '12px' }}>
                    <button type="button" onClick={handleAddRoomType} className="btn btn-primary">
                      {editingRoomTypeIndex !== null ? 'Update Room Type' : 'Add Room Type'}
                    </button>
                    {editingRoomTypeIndex !== null && (
                      <button type="button" onClick={handleCancelEditRoomType} className="btn btn-secondary">
                        Cancel
                      </button>
                    )}
                  </div>
                </div>

                <div className="room-types-list" style={{ marginTop: '32px' }}>
                  <h3>Added Room Types</h3>
                  {roomTypes.length === 0 ? (
                    <div style={{ 
                      padding: '24px', 
                      textAlign: 'center', 
                      backgroundColor: '#fef3c7', 
                      border: '1px solid #fbbf24', 
                      borderRadius: '8px',
                      color: '#92400e'
                    }}>
                      <p style={{ margin: 0, fontWeight: '500' }}>⚠️ No room types added yet. Please add at least one room type.</p>
                    </div>
                  ) : (
                    roomTypes.map((roomType, index) => (
                    <div key={index} className="room-type-item">
                      <div>
                        <strong>{roomType.roomType}</strong> - {formData.currency} {roomType.pricePerNight}/night
                        {roomType.cutPrice && (
                          <span style={{ textDecoration: 'line-through', color: '#64748b', marginLeft: '8px' }}>
                            {formData.currency} {roomType.cutPrice}
                          </span>
                        )}
                        <br />
                        Max {roomType.maxPersons} persons
                        {roomType.bedType && <span> • {roomType.bedType} bed</span>}
                        {roomType.roomSize && <span> • {roomType.roomSize}</span>}
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          type="button"
                          onClick={() => handleEditRoomType(index)}
                          className="btn btn-secondary"
                          style={{ padding: '4px 8px', fontSize: '12px' }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleRemoveRoomType(index)}
                          className="btn btn-danger"
                          style={{ padding: '4px 8px', fontSize: '12px' }}
                        >
                          Remove
                        </button>
                      </div>
                    </div>
                  ))
                  )}
                </div>
              </div>
            )}

            {/* Media Tab */}
            {activeTab === 'media' && (
              <div className="form-section">
                <h2>Images *</h2>
                <p className="form-hint" style={{ marginBottom: '24px', color: '#64748b' }}>
                  At least one image is required to create a hotel listing.
                </p>
                
                {/* Upload Section */}
                <div className="form-group">
                  <label>Upload Images</label>
                  <div className="image-upload-area">
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleImageChange}
                      id="hotel-image-upload"
                      className="image-upload-input"
                    />
                    <label htmlFor="hotel-image-upload" className="image-upload-label">
                      <div className="image-upload-content">
                        <span className="image-upload-icon">📷</span>
                        <span className="image-upload-text">Click to upload or drag and drop</span>
                        <span className="image-upload-hint">PNG, JPG, GIF up to 5MB</span>
                      </div>
                    </label>
                  </div>
                </div>

                {/* New Images Preview */}
                {imagePreviews.length > 0 && (
                  <div className="images-preview-section">
                    <h3>Selected Images ({imagePreviews.length})</h3>
                    <div className="images-grid">
                      {imagePreviews.map((preview, index) => (
                        <div key={index} className="image-preview-card">
                          <div className="image-preview-wrapper">
                            <img src={preview} alt={`Preview ${index + 1}`} />
                            {featuredImage === `new_image_${index}` && (
                              <div className="featured-badge-overlay">
                                <span className="featured-badge-text">⭐ Featured</span>
                              </div>
                            )}
                          </div>
                          <div className="image-preview-actions">
                            <button
                              type="button"
                              onClick={() => handleSetFeaturedFromNew(index)}
                              className={`btn btn-sm ${featuredImage === `new_image_${index}` ? 'btn-primary' : 'btn-secondary'}`}
                            >
                              {featuredImage === `new_image_${index}` ? '⭐ Featured' : 'Set as Featured'}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRemoveNewImage(index)}
                              className="btn btn-sm btn-danger"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Existing Images */}
                {existingImages.length > 0 && (
                  <div className="images-preview-section">
                    <h3>Existing Images ({existingImages.length})</h3>
                    <div className="images-grid">
                      {existingImages.map((image, index) => (
                        <div key={index} className="image-preview-card">
                          <div className="image-preview-wrapper">
                            <img src={`${API_BASE_URL_WITHOUT_API}${image}`} alt={`Hotel ${index + 1}`} />
                            {featuredImage === image && (
                              <div className="featured-badge-overlay">
                                <span className="featured-badge-text">⭐ Featured</span>
                              </div>
                            )}
                          </div>
                          <div className="image-preview-actions">
                            <button
                              type="button"
                              onClick={() => handleSetFeaturedImage(image)}
                              className={`btn btn-sm ${featuredImage === image ? 'btn-primary' : 'btn-secondary'}`}
                            >
                              {featuredImage === image ? '⭐ Featured' : 'Set as Featured'}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleRemoveExistingImage(index)}
                              className="btn btn-sm btn-danger"
                            >
                              Remove
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Empty State for Images */}
                {imagePreviews.length === 0 && existingImages.length === 0 && (
                  <div style={{ 
                    padding: '24px', 
                    textAlign: 'center', 
                    backgroundColor: '#fef3c7', 
                    border: '1px solid #fbbf24', 
                    borderRadius: '8px',
                    color: '#92400e',
                    marginTop: '24px'
                  }}>
                    <p style={{ margin: 0, fontWeight: '500' }}>⚠️ No images uploaded yet. Please upload at least one image.</p>
                  </div>
                )}

                {/* No Images Message */}
                {existingImages.length === 0 && imagePreviews.length === 0 && (
                  <div className="no-images-message">
                    <span className="no-images-icon">🖼️</span>
                    <p>No images uploaded yet. Upload images to get started.</p>
                  </div>
                )}
              </div>
            )}

            {/* Additional Tab */}
            {activeTab === 'social' && (
              <div className="form-section">
                <h2>Social Media Links</h2>
                <p className="form-hint" style={{ marginBottom: '24px' }}>
                  Add your social media profiles to help customers connect with you.
                </p>
                
                <div className="form-group">
                  <label>
                    <span className="social-icon">📘</span>
                    Facebook URL
                  </label>
                  <input
                    type="url"
                    name="socialLinks.facebook"
                    value={formData.socialLinks.facebook}
                    onChange={handleChange}
                    placeholder="https://www.facebook.com/yourpage"
                  />
                </div>

                <div className="form-group">
                  <label>
                    <span className="social-icon">📷</span>
                    Instagram URL
                  </label>
                  <input
                    type="url"
                    name="socialLinks.instagram"
                    value={formData.socialLinks.instagram}
                    onChange={handleChange}
                    placeholder="https://www.instagram.com/yourprofile"
                  />
                </div>

                <div className="form-group">
                  <label>
                    <span className="social-icon">📺</span>
                    YouTube URL
                  </label>
                  <input
                    type="url"
                    name="socialLinks.youtube"
                    value={formData.socialLinks.youtube}
                    onChange={handleChange}
                    placeholder="https://www.youtube.com/channel/yourchannel"
                  />
                </div>
              </div>
            )}

            {activeTab === 'additional' && (
              <div className="form-section">
                <h2>Additional Information</h2>
                
                <div className="form-group">
                  <label>Nearby Attractions</label>
                  <div className="attraction-input">
                    <input
                      type="text"
                      placeholder="Attraction Name"
                      value={attractionInput.name}
                      onChange={(e) => setAttractionInput({ ...attractionInput, name: e.target.value })}
                    />
                    <input
                      type="text"
                      placeholder="Distance"
                      value={attractionInput.distance}
                      onChange={(e) => setAttractionInput({ ...attractionInput, distance: e.target.value })}
                    />
                    <input
                      type="text"
                      placeholder="Description"
                      value={attractionInput.description}
                      onChange={(e) => setAttractionInput({ ...attractionInput, description: e.target.value })}
                    />
                    <button type="button" onClick={handleAddAttraction} className="btn btn-secondary">
                      Add
                    </button>
                  </div>
                  <div className="attractions-list">
                    {nearbyAttractions.map((attraction, index) => (
                      <div key={index} className="attraction-item">
                        <strong>{attraction.name}</strong>
                        {attraction.distance && <span> - {attraction.distance}</span>}
                        {attraction.description && <p>{attraction.description}</p>}
                        <button
                          type="button"
                          onClick={() => handleRemoveAttraction(index)}
                          className="btn btn-danger"
                          style={{ fontSize: '12px', padding: '4px 8px', marginTop: '8px' }}
                        >
                          Remove
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="form-group">
                  <label>Languages Spoken</label>
                  <div className="amenity-input">
                    <input
                      type="text"
                      value={languageInput}
                      onChange={(e) => setLanguageInput(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddLanguage())}
                      placeholder="e.g., English, Hindi"
                    />
                    <button type="button" onClick={handleAddLanguage} className="btn btn-secondary">
                      Add
                    </button>
                  </div>
                  <div className="amenity-list">
                    {languages.map((language, index) => (
                      <span key={index} className="badge badge-primary">
                        {language}
                        <button
                          type="button"
                          onClick={() => handleRemoveLanguage(index)}
                          className="remove-btn"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Form Actions */}
          <div className="form-actions">
            {isLastTab ? (
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? 'Saving...' : isEdit ? 'Update Hotel' : 'Create Hotel'}
              </button>
            ) : (
              <button 
                type="button" 
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleNext(e);
                }} 
                className="btn btn-primary"
              >
                Next
              </button>
            )}
            <button type="button" onClick={handleCancel} className="btn btn-secondary">
              Cancel
            </button>
          </div>
        </form>
      </div>
  );

  return isOwnerMode ? content : <Layout>{content}</Layout>;
};

export default HotelForm;
